import React, { useState } from "react";
import EmailBody, { EmailFrame } from "./EmailBody";
import { openDocument } from "./dealApi";
import { formatBytes, formatDateTime, formatRole } from "./dealFormat";

const SOURCE_LABELS = {
  generated: "Generated",
  email_attachment: "Email attachment",
};

const toggled = (set, id) => {
  const next = new Set(set);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};

// Timeline / Emails / Documents for one deal — and Drafts when `drafts` is
// passed, which only the admin page does. Staff-only email fields
// (email_type, approved_by) render only when the API supplied them.
// A marketing session reuses it by passing its own `apiPath`.
const DealHistory = ({
  dealId,
  apiPath = `/api/deals/${dealId}`,
  emails,
  documents,
  timeline,
  drafts,
}) => {
  const [tab, setTab] = useState("timeline");
  const [openEmails, setOpenEmails] = useState(new Set());
  const [openDrafts, setOpenDrafts] = useState(new Set());
  const [downloadError, setDownloadError] = useState(null);

  const tabs = ["timeline", "emails", "documents"];
  if (Array.isArray(drafts)) tabs.push("drafts");

  const download = async (documentId) => {
    setDownloadError(null);
    try {
      await openDocument(apiPath, documentId);
    } catch (err) {
      setDownloadError(err.message);
    }
  };

  const renderDownload = (documentId) => (
    <button
      className="deal-history-download"
      onClick={() => download(documentId)}
    >
      Download
    </button>
  );

  const renderTimeline = () =>
    timeline.length === 0 ? (
      <p className="deal-details-empty">No timeline events yet.</p>
    ) : (
      <ul className="deal-history-list">
        {timeline.map((entry, i) => (
          <li key={`${entry.kind}-${entry.at}-${i}`} className="deal-history-row">
            <span className="deal-history-when">{formatDateTime(entry.at)}</span>
            <span>{entry.summary}</span>
          </li>
        ))}
      </ul>
    );

  const renderEmails = () =>
    emails.length === 0 ? (
      <p className="deal-details-empty">No emails yet.</p>
    ) : (
      <ul className="deal-history-list">
        {emails.map((email) => {
          const isOpen = openEmails.has(email.id);
          const attachments = documents.filter((d) =>
            email.document_ids.includes(d.id)
          );
          return (
            <li key={email.id} className="deal-history-row deal-history-email">
              <button
                className="deal-history-toggle"
                onClick={() => setOpenEmails(toggled(openEmails, email.id))}
              >
                <span className="deal-history-direction">
                  {email.direction === "inbound" ? "Received" : "Sent"}
                </span>
                <span className="deal-history-subject">
                  {email.subject || "(no subject)"}
                </span>
                <span className="deal-history-party">
                  {email.sender} → {email.recipient}
                </span>
                <span className="deal-history-when">
                  {formatDateTime(email.at)}
                </span>
              </button>
              {(email.email_type || email.approved_by) && (
                <div className="deal-history-meta">
                  {email.email_type && formatRole(email.email_type)}
                  {email.approved_by && ` · approved by ${email.approved_by}`}
                </div>
              )}
              {attachments.map((doc) => (
                <div key={doc.id} className="deal-history-attachment">
                  {doc.filename} {renderDownload(doc.id)}
                </div>
              ))}
              {isOpen && <EmailBody apiPath={apiPath} messageId={email.id} />}
            </li>
          );
        })}
      </ul>
    );

  const renderDocuments = () =>
    documents.length === 0 ? (
      <p className="deal-details-empty">No documents yet.</p>
    ) : (
      <ul className="deal-history-list">
        {documents.map((doc) => (
          <li key={doc.id} className="deal-history-row">
            <span className="deal-history-subject">{doc.filename}</span>
            <span className="deal-history-meta">
              {SOURCE_LABELS[doc.source] || doc.source}
              {doc.size_bytes !== null && ` · ${formatBytes(doc.size_bytes)}`}
            </span>
            <span className="deal-history-when">
              {formatDateTime(doc.created_at)}
            </span>
            {renderDownload(doc.id)}
          </li>
        ))}
      </ul>
    );

  const renderDrafts = () =>
    drafts.length === 0 ? (
      <p className="deal-details-empty">No drafts.</p>
    ) : (
      <ul className="deal-history-list">
        {drafts.map((draft) => (
          <li key={draft.id} className="deal-history-row deal-history-email">
            <button
              className="deal-history-toggle"
              onClick={() => setOpenDrafts(toggled(openDrafts, draft.id))}
            >
              <span className="deal-history-direction">
                {formatRole(draft.email_type)} · {draft.status}
              </span>
              <span className="deal-history-subject">{draft.subject}</span>
              <span className="deal-history-party">to {draft.to_email}</span>
              <span className="deal-history-when">
                {formatDateTime(draft.created_at)}
              </span>
            </button>
            {openDrafts.has(draft.id) && (
              <EmailFrame html={draft.html_content} text="" />
            )}
          </li>
        ))}
      </ul>
    );

  const panels = {
    timeline: renderTimeline,
    emails: renderEmails,
    documents: renderDocuments,
    drafts: renderDrafts,
  };

  return (
    <div className="deal-details-section">
      <div className="deal-history-tabs" role="tablist">
        {tabs.map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={tab === name}
            className={`deal-history-tab${tab === name ? " deal-history-tab-active" : ""}`}
            onClick={() => setTab(name)}
          >
            {name.charAt(0).toUpperCase() + name.slice(1)}
          </button>
        ))}
      </div>
      {downloadError && <p className="error">Error: {downloadError}</p>}
      {panels[tab]()}
    </div>
  );
};

export default DealHistory;
